import React, { HTMLAttributes } from 'react';
import {
  Autocomplete,
  CircularProgress,
  IconButton,
  TextField,
  TextFieldProps,
  Typography
} from '@mui/material';
import { useToggle } from '@ianesthesie-kit/core';
import {
  useDeleteSurgeonNameFromGlobalListMutation,
  useSurgeonNameGlobalList
} from '@/modules/surgicalEvent';
import { styled } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';

const renderTags = (value: string[]) => (
  <Typography component="span" pl={1}>
    {value.join(', ')}
  </Typography>
);

const renderOption = ({ key, ...props }: any, option: string) => (
  <DeletableOption key={key} {...props}>
    {option}
  </DeletableOption>
);

const StyledListItem = styled('li')`
  display: flex;
  align-items: center;
  justify-content: space-between !important;
  gap: 12px;

  & > div:last-child {
    min-width: 20px;
    max-width: 20px;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    > {
      width: 100%;
    }
  }

  button {
    opacity: 0.6;
    padding: 2px;
    transform: rotate(0);
    transition: all 0.15s 0.1s ease-in;

    &:hover {
      opacity: 1;
      transform: rotate(90deg);
      transition-delay: 0s;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

type DeletableOptionProps = Omit<HTMLAttributes<HTMLLIElement>, 'children'> & {
  children: string;
};
const DeletableOption = ({
  children: surgeonName,
  ...props
}: DeletableOptionProps) => {
  const [deleteSurgeonName, { isLoading }] =
    useDeleteSurgeonNameFromGlobalListMutation();

  return (
    <StyledListItem {...props}>
      <span>{surgeonName}</span>
      <div>
        {isLoading && <CircularProgress color="inherit" size={16} />}
        {!isLoading && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteSurgeonName({ name: surgeonName });
            }}
          >
            <ClearIcon />
          </IconButton>
        )}
      </div>
    </StyledListItem>
  );
};

export type SurgeonNameAutocompleteProps = {
  value?: string[];
  onChange: (v: string[]) => void;
  loading?: boolean;
  label: string;
  error?: string;
  readOnly?: boolean;
  textFieldProps?: TextFieldProps;
};

export const SurgeonNameAutocomplete = ({
  value = [],
  onChange,
  loading,
  label,
  error,
  readOnly,
  textFieldProps
}: SurgeonNameAutocompleteProps) => {
  const { toggledOn: isOpen, toggleOn: open, toggleOff: close } = useToggle();
  const { data: surgeonNames = [], isLoading: queryLoading } =
    useSurgeonNameGlobalList(undefined, {
      skip: !isOpen
    });

  return (
    <Autocomplete
      freeSolo
      multiple
      fullWidth
      filterSelectedOptions
      selectOnFocus
      clearOnBlur
      disableClearable
      open={isOpen}
      onOpen={open}
      onClose={close}
      readOnly={readOnly}
      size="small"
      color="riskNone"
      loading={loading || queryLoading}
      options={surgeonNames}
      renderInput={(p) => (
        <TextField
          error={!!error}
          {...p}
          label={label}
          color="riskNone"
          size="small"
          {...textFieldProps}
        />
      )}
      renderOption={renderOption}
      renderTags={renderTags}
      value={value}
      onChange={(e, v) => onChange(v)}
    />
  );
};
